var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, debounce, InputAdornment, Popover, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Search01Icon } from 'hugeicons-react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalSearchModule } from '../../../application/common/enums/global-search-module';
import { useLazyGetGlobalSearchAllQuery, useLazyGetGlobalSearchFilesQuery } from '../../../application/features/global-search/global-search-api';
import { useAppSelector } from '../../../application/store/useStore';
import useTranslate from '../../../localization/helpers';
import FileViewer from '../../components/FileViewer';
import SearchResult from './search-result';
var GlobalSearch = function () {
    var _a, _b;
    var theme = useTheme();
    var location = useLocation();
    var islg = useMediaQuery(theme.breakpoints.up('lg'));
    var isIpadMini = useMediaQuery('(max-width: 768px)');
    var translate = useTranslate().translate;
    var inputRef = useRef(null);
    var currentUser = useAppSelector(function (state) { return state.auth; }).currentUser;
    var _c = __read(useState(''), 2), searchText = _c[0], setSearchText = _c[1];
    var _d = __read(useState(null), 2), searchAnchorEl = _d[0], setSearchAnchorEl = _d[1];
    var _e = __read(useState(false), 2), isInDepthEnabled = _e[0], setIsInDepthEnabled = _e[1];
    var _f = __read(useState(false), 2), isInDepthLoading = _f[0], setIsInDepthLoading = _f[1];
    var _g = __read(useState(GlobalSearchModule.All), 2), currentModule = _g[0], setCurrentModule = _g[1];
    var _h = __read(useState(undefined), 2), combinedResults = _h[0], setCombinedResults = _h[1];
    var _j = __read(useState({
        searchText: '',
        module: GlobalSearchModule.All,
        enablePagination: true,
        userId: (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId) !== null && _a !== void 0 ? _a : ''
    }), 2), searchAllRequest = _j[0], setSearchAllRequest = _j[1];
    var _k = __read(useState({
        searchText: '',
        enablePagination: true,
        userId: (_b = currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId) !== null && _b !== void 0 ? _b : ''
    }), 2), searchFilesQuery = _k[0], setSearchFilesQuery = _k[1];
    var _l = __read(useLazyGetGlobalSearchAllQuery(), 2), globalSearchAll = _l[0], _m = _l[1], allResults = _m.data, isAllFetching = _m.isFetching, isAllSuccess = _m.isSuccess;
    var _o = __read(useLazyGetGlobalSearchFilesQuery(), 2), globalSearchFiles = _o[0], _p = _o[1], filesResults = _p.data, isFilesFetching = _p.isFetching;
    useEffect(function () {
        setCombinedResults(allResults
            ? __assign(__assign({}, allResults), { files: isInDepthEnabled && filesResults ? filesResults.files : allResults.files, fileCount: isInDepthEnabled && filesResults ? filesResults.fileCount : allResults.fileCount, totalCount: (function () {
                    var _a, _b;
                    if (isInDepthEnabled && currentModule === GlobalSearchModule.All && filesResults) {
                        return allResults.totalCount + filesResults.fileCount;
                    }
                    else if (currentModule === GlobalSearchModule.Files) {
                        return (_a = filesResults === null || filesResults === void 0 ? void 0 : filesResults.fileCount) !== null && _a !== void 0 ? _a : 0;
                    }
                    else {
                        return (_b = allResults.totalCount) !== null && _b !== void 0 ? _b : 0;
                    }
                })() }) : undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allResults, currentModule, filesResults, searchAllRequest]);
    var isCombinedFetching = isAllFetching || (isInDepthEnabled && isFilesFetching);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var getGlobalSearchAllResults = useCallback(debounce(function (request) {
        globalSearchAll(request);
    }, 300), [globalSearchAll]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var getGlobalSearchFilesResults = useCallback(debounce(function (request) {
        globalSearchFiles(request);
    }, 300), [globalSearchFiles]);
    useEffect(function () {
        if (searchAllRequest && searchAllRequest.searchText.trim().length >= 2) {
            getGlobalSearchAllResults(searchAllRequest);
        }
    }, [searchAllRequest, getGlobalSearchAllResults]);
    useEffect(function () {
        if (isInDepthEnabled && searchFilesQuery && searchFilesQuery.searchText.trim().length >= 2) {
            setIsInDepthLoading(true);
            getGlobalSearchFilesResults(searchFilesQuery);
        }
    }, [searchFilesQuery, getGlobalSearchFilesResults, isInDepthEnabled]);
    useEffect(function () {
        if (!isFilesFetching && isInDepthLoading) {
            setIsInDepthLoading(false);
        }
    }, [isFilesFetching, isInDepthLoading]);
    var searchOpen = Boolean(searchAnchorEl) && (Boolean(combinedResults) || isCombinedFetching);
    var handleClose = function () {
        setSearchAnchorEl(null);
        setIsInDepthEnabled(false);
    };
    var handleEnterPress = function (event) {
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId) {
            if (event.key === 'Enter' && searchText.trim().length >= 2) {
                var request = {
                    searchText: searchText,
                    module: GlobalSearchModule.All,
                    enablePagination: true,
                    userId: currentUser.userId
                };
                setSearchAllRequest(request);
                if (isInDepthEnabled) {
                    var filesRequest = {
                        searchText: searchText,
                        enablePagination: true,
                        userId: currentUser.userId
                    };
                    setSearchFilesQuery(filesRequest);
                }
                globalSearchAll(request);
                if (inputRef.current) {
                    setSearchAnchorEl(inputRef.current);
                }
                setTimeout(function () {
                    var _a;
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 100);
            }
        }
    };
    var handleInputChange = function (e) {
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId) {
            var value = e.target.value;
            setSearchText(value);
            if (value.trim().length >= 2) {
                var request = {
                    searchText: value,
                    module: GlobalSearchModule.All,
                    enablePagination: true,
                    userId: currentUser.userId
                };
                setSearchAllRequest(request);
                if (isInDepthEnabled) {
                    var filesRequest = {
                        searchText: value,
                        enablePagination: true,
                        userId: currentUser.userId
                    };
                    setSearchFilesQuery(filesRequest);
                }
                setSearchAnchorEl(inputRef.current);
                setTimeout(function () {
                    var _a;
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 100);
            }
            else {
                setSearchAllRequest(null);
                setSearchFilesQuery(null);
                setSearchAnchorEl(null);
            }
        }
    };
    var handleModuleChange = function (module) {
        var _a;
        setCurrentModule(module);
        if ((currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId) && module !== GlobalSearchModule.Files) {
            var request = {
                searchText: (_a = searchAllRequest === null || searchAllRequest === void 0 ? void 0 : searchAllRequest.searchText) !== null && _a !== void 0 ? _a : '',
                module: module,
                enablePagination: true,
                userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId
            };
            setSearchAllRequest(request);
        }
    };
    var handleInDepthToggle = function (enabled) {
        setIsInDepthEnabled(enabled);
        if (enabled && searchText.trim().length >= 2 && (currentUser === null || currentUser === void 0 ? void 0 : currentUser.userId)) {
            var filesRequest = {
                searchText: searchText,
                enablePagination: true,
                userId: currentUser.userId
            };
            setSearchFilesQuery(filesRequest);
        }
    };
    useEffect(function () {
        if (!location.pathname.includes('search')) {
            setSearchText('');
        }
    }, [location]);
    return (_jsxs(Fragment, { children: [_jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(TextField, { ref: inputRef, size: 'small', fullWidth: true, variant: 'outlined', placeholder: translate('actions.search'), value: searchText, sx: { width: islg ? '350px' : undefined, zIndex: 1301 }, autoFocus: true, onChange: handleInputChange, onKeyDown: handleEnterPress, inputRef: inputRef, slotProps: {
                        input: {
                            startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(Search01Icon, { size: 20 }) }))
                        }
                    } }) }), _jsx(Popover, { open: searchOpen, onClose: handleClose, anchorEl: searchAnchorEl, disablePortal: true, slotProps: { paper: { sx: { borderRadius: '20px' } } }, sx: {
                    left: {
                        xs: '0px',
                        sm: isIpadMini ? '-155px' : undefined,
                        md: '-170px',
                        zIndex: theme.zIndex.drawer - 1
                    },
                    boxShadow: theme.shadows[24]
                }, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                }, disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true, children: _jsx(Box, { gap: '16px', display: 'flex', flexDirection: 'column', sx: { width: { xs: '350px', sm: isIpadMini ? '445px' : undefined, md: '596px' } }, marginTop: 1, children: _jsx(SearchResult, { closePopover: handleClose, data: combinedResults, searchTerm: searchText, handleModuleChange: handleModuleChange, isFetching: isAllFetching, isSuccess: isAllSuccess, isInDepthEnabled: isInDepthEnabled, onInDepthToggle: handleInDepthToggle, isInDepthLoading: isInDepthEnabled ? isFilesFetching : false }) }) }), _jsx(FileViewer, {})] }));
};
export default GlobalSearch;
